import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Tag, message, Progress, Table, Modal } from 'dingtalk-design-desktop';
import type { MatchingResult, FieldInfo, MatchedRecord } from '../types';
import { renderMessageTemplate, convertToMarkdown } from '../services/messageFormatter';
import ReactMarkdown from 'react-markdown';
import { createPlatformAdapter } from '../adapters/PlatformFactory';
import { countMessageStatus } from '../services/api';

interface MessageSendPreviewProps {
  matchingResult: MatchingResult;
  fields: FieldInfo[];
  messageTemplate: string;
}

export const MessageSendPreview: React.FC<MessageSendPreviewProps> = ({
  matchingResult,
  fields,
  messageTemplate,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const sendLockedRef = useRef(false);
  const confirmationRef = useRef<ReturnType<typeof Modal.confirm> | null>(null);
  const [messageIds, setMessageIds] = useState<number[]>([]);
  const [sendStatus, setSendStatus] = useState({
    totalCount: 0,
    sentCount: 0,
    sendingCount: 0,
    pendingCount: 0,
  });
  const platform = createPlatformAdapter();

  // 离开发送预览时关闭确认框，避免继续提交旧页面的消息。
  useEffect(() => () => {
    const confirmation = confirmationRef.current;
    confirmationRef.current = null;
    confirmation?.destroy();
  }, []);

  // 获取所有待发送记录（已匹配分组展开）
  const allRecords = useMemo(() => 
    matchingResult.groups.flatMap(g => g.records), 
    [matchingResult]
  );

  // 渲染消息内容
  const getMessageContent = (record: any) => renderMessageTemplate(messageTemplate, record, fields);

  // 表格数据
  const tableData = useMemo(() => 
    allRecords.map((record, index) => ({
      ...record,
      key: record.id,
      _index: index + 1,
      _messageContent: getMessageContent(record),
    })),
    [allRecords, messageTemplate, fields]
  );

  // 轮询统计发送状态
  useEffect(() => {
    if (messageIds.length === 0) return;
    
    let pollCount = 0;
    const maxPollCount = 30;
    
    const pollStatus = async () => {
      try {
        const status = await countMessageStatus(messageIds);
        setSendStatus(status);
        
        if (status.sentCount === status.totalCount && status.totalCount > 0) {
          clearInterval(timer);
          return;
        }
        
        pollCount++;
        if (pollCount >= maxPollCount) {
          clearInterval(timer);
        }
      } catch (e) {
        console.error('统计发送状态失败:', e);
      }
    };

    pollStatus();
    const timer = setInterval(pollStatus, 5000);
    return () => clearInterval(timer);
  }, [messageIds]);

  // 所有发送入口统一确认，并同步加锁，防止快速连点弹出多个确认框。
  const confirmSend = (records: MatchedRecord[], label: string, onConfirm: () => Promise<void>) => {
    if (sendLockedRef.current || records.length === 0) return;
    sendLockedRef.current = true;
    setConfirming(true);
    confirmationRef.current = Modal.confirm({
      title: `确认${label}？`,
      content: `将发送 ${records.length} 条消息。发送后不可撤回，请确认接收群和消息内容。`,
      okText: '确认发送',
      cancelText: '取消',
      // 显式点击取消时同步失效，避免 Esc 关闭动画期间仍可触发旧确认。
      keyboard: false,
      onOk: () => confirmationRef.current ? onConfirm() : undefined,
      onCancel: () => {
        confirmationRef.current = null;
      },
      afterClose: () => {
        sendLockedRef.current = false;
        confirmationRef.current = null;
        setConfirming(false);
      },
    });
  };

  // 单条发送
  const handleSendSingle = async (record: any) => {
    setSending(true);
    try {
      const messageId = await platform.sendMessage({
        messageTemplateId: 135,
        content: getMessageContent(record),
        needSendTime: new Date().toISOString().slice(0, 19).replace('T', ' '),
        status: 0,
        title: '日常信息提醒',
        receiverId: record.receiverId ?? 0,
        atList: '',
        employeeId: 0,
      });
      if (messageId) {
        setMessageIds(prev => [...prev, messageId]);
      }
      message.success('发送成功');
    } catch (e) {
      message.error('发送失败');
    }
    setSending(false);
  };

  // 多选发送
  const handleSendSelected = async () => {
    setSending(true);
    const ids: number[] = [];
    try {
      for (const record of allRecords.filter(r => selectedRowKeys.includes(r.id))) {
        const messageId = await platform.sendMessage({
          messageTemplateId: 135,
          content: getMessageContent(record),
          needSendTime: new Date().toISOString().slice(0, 19).replace('T', ' '),
          status: 0,
          title: '日常信息提醒',
          receiverId: record.receiverId ?? 0,
          atList: '',
          employeeId: 0,
        });
        if (messageId) {
          ids.push(messageId);
        }
      }
      if (ids.length > 0) {
        setMessageIds(prev => [...prev, ...ids]);
      }
      message.success('多选发送成功');
    } catch (e) {
      message.error('多选发送失败');
    }
    setSending(false);
  };

  // 全量发送
  const handleSendAll = async () => {
    setSending(true);
    const ids: number[] = [];
    try {
      for (const record of allRecords) {
        const messageId = await platform.sendMessage({
          messageTemplateId: 135,
          content: getMessageContent(record),
          needSendTime: new Date().toISOString().slice(0, 19).replace('T', ' '),
          status: 0,
          title: '日常信息提醒',
          receiverId: record.receiverId ?? 0,
          atList: '',
          employeeId: 0,
        });
        if (messageId) {
          ids.push(messageId);
        }
      }
      if (ids.length > 0) {
        setMessageIds(prev => [...prev, ...ids]);
      }
      message.success('全量发送成功');
    } catch (e) {
      message.error('全量发送失败');
    }
    setSending(false);
  };

  // 表格选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  // 表格列定义
  const columns = [
    {
      title: '序号',
      dataIndex: '_index',
      key: '_index',
      width: 50,
      align: 'center' as const,
    },
    {
      title: '群名',
      dataIndex: 'groupName',
      key: 'groupName',
      width: 160,
      render: (text: string) => (
        <div style={{ fontWeight: 500, color: '#333', whiteSpace: 'normal', wordBreak: 'break-all' }}>
          {text}
        </div>
      ),
    },
    {
      title: '消息内容',
      dataIndex: '_messageContent',
      key: '_messageContent',
      render: (text: string) => {
        const markdown = convertToMarkdown(text);
        return (
          <div className="msg-content">
            <ReactMarkdown>{markdown}</ReactMarkdown>
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 70,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <button 
          onClick={() => confirmSend([record], `发送给「${record.groupName || record.receiverName || '当前群'}」`, () => handleSendSingle(record))}
          disabled={sending || confirming}
          className="table-send-btn"
        >
          发送
        </button>
      ),
    },
  ];

  return (
    <div className="send-preview">
      <style>{`
        .send-preview {
          background: #fff;
          padding: 10px;
          border-radius: 6px;
          border: 1px solid #e8e8e8;
        }
        .send-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px;
          padding: 8px;
          margin-bottom: 10px;
          background: #fafafa;
          border-radius: 4px;
          border: 1px solid #eee;
        }
        .send-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .send-btns {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .action-btn {
          padding: 4px 10px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          background: #1890ff;
          color: #fff;
          font-size: 12px;
          transition: all 0.2s;
        }
        .action-btn:hover:not(:disabled) {
          background: #40a9ff;
        }
        .action-btn:disabled {
          background: #d9d9d9;
          cursor: not-allowed;
        }
        .progress-box {
          margin-bottom: 10px;
          padding: 10px;
          background: #fafafa;
          border-radius: 4px;
          border: 1px solid #eee;
        }
        .progress-box h4 {
          margin: 0 0 8px 0;
          font-size: 13px;
          font-weight: 500;
        }
        .progress-info {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 8px;
          font-size: 12px;
        }
        .table-box {
          border: 1px solid #eee;
          border-radius: 4px;
          overflow: hidden;
        }
        .msg-content {
          font-size: 13px;
          line-height: 1.7;
          color: #333;
          text-align: left;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .msg-content h1, .msg-content h2, .msg-content h3, 
        .msg-content h4, .msg-content h5, .msg-content h6 {
          font-size: 14px;
          font-weight: 600;
          margin: 0 0 6px 0;
          color: #1890ff;
        }
        .msg-content p {
          margin: 0 0 6px 0;
          white-space: pre-wrap;
        }
        .msg-content p:last-child {
          margin-bottom: 0;
        }
        .send-btn {
          padding: 3px 8px;
          border: none;
          border-radius: 3px;
          cursor: pointer;
          background: #1890ff;
          color: #fff;
          font-size: 11px;
        }
        .send-btn:hover:not(:disabled) {
          background: #40a9ff;
        }
        .send-btn:disabled {
          background: #d9d9d9;
          cursor: not-allowed;
        }
        .table-send-btn {
          padding: 5px 14px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          background: #1890ff;
          color: #fff;
          font-size: 13px;
          white-space: nowrap;
        }
        .table-send-btn:hover:not(:disabled) {
          background: #40a9ff;
        }
        .table-send-btn:disabled {
          background: #d9d9d9;
          cursor: not-allowed;
        }
        .send-preview .dd-table {
          font-size: 13px;
        }
        .send-preview .dd-table-thead > tr > th {
          background: #fafafa;
          font-weight: 500;
          padding: 10px 12px;
          font-size: 13px;
        }
        .send-preview .dd-table-tbody > tr > td {
          padding: 12px;
          vertical-align: top;
        }
        /* 列宽优化 */
        .send-preview .dd-table colgroup col:nth-child(1) { width: 40px !important; }  /* 选择框 */
        .send-preview .dd-table colgroup col:nth-child(2) { width: 50px !important; }  /* 序号 */
        .send-preview .dd-table colgroup col:nth-child(3) { width: 160px !important; } /* 群名 */
        .send-preview .dd-table colgroup col:nth-child(5) { width: 70px !important; }  /* 操作 */
        @media (max-width: 360px) {
          .send-header {
            flex-direction: column;
            align-items: flex-start;
          }
          .send-preview .dd-table {
            font-size: 11px;
          }
        }
      `}</style>

      {/* 顶部统计和操作栏 */}
      <div className="send-header">
        <div className="send-tags">
          <Tag color="blue">{matchingResult.groups.length} 个群</Tag>
          <Tag color="green">{allRecords.length} 条消息</Tag>
          {selectedRowKeys.length > 0 && (
            <Tag color="orange">已选 {selectedRowKeys.length}</Tag>
          )}
        </div>
        <div className="send-btns">
          <button onClick={() => confirmSend(allRecords, '全量发送', handleSendAll)} disabled={sending || confirming || allRecords.length === 0} className="action-btn">
            全量发送
          </button>
          <button 
            onClick={() => confirmSend(allRecords.filter(record => selectedRowKeys.includes(record.id)), '多选发送', handleSendSelected)}
            disabled={sending || confirming || selectedRowKeys.length === 0}
            className="action-btn"
          >
            多选发送
          </button>
        </div>
      </div>
      
      {/* 发送进度 */}
      {messageIds.length > 0 && (
        <div className="progress-box">
          <h4>发送进度</h4>
          <div className="progress-info">
            <span>总计: <b>{sendStatus.totalCount}</b></span>
            <span style={{ color: '#52c41a' }}>已发送: <b>{sendStatus.sentCount}</b></span>
            <span style={{ color: '#1890ff' }}>发送中: <b>{sendStatus.sendingCount}</b></span>
            <span style={{ color: '#faad14' }}>待发送: <b>{sendStatus.pendingCount}</b></span>
          </div>
          <Progress 
            percent={sendStatus.totalCount > 0 ? Math.round((sendStatus.sentCount / sendStatus.totalCount) * 100) : 0} 
            status={sendStatus.sentCount === sendStatus.totalCount && sendStatus.totalCount > 0 ? 'success' : 'active'}
            size="small"
          />
        </div>
      )}
      
      {/* 数据表格 */}
      <div className="table-box">
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={tableData}
          pagination={false}
          scroll={{ x: 400, y: 300 }}
          size="small"
        />
      </div>
    </div>
  );
}

export default MessageSendPreview;
